import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import AvatarEditor from 'react-avatar-editor';

import DialogHeader from 'components/DialogHeader';

import { muiThemeLuma } from 'themes';

const styles = (theme) => {
  const {
    custom: {
      palette: { colorGrayishBlue },
    },
    spacing: { unit },
  } = theme;

  return {
    bottom: {},
    button: {
      width: 112,
    },
    canvasEditor: {
      borderRadius: 112,
      margin: 0,
    },
    canvasEditorWrapper: {
      display: 'inline-block',
      float: 'left',
      position: 'relative',
      width: 270,
    },
    dialog: {
      borderRadius: 0,
      minHeight: 400,
      minWidth: 500,
    },
    dialogContent: {
      display: 'block',
      minHeight: 245,
      overflow: 'hidden',
      padding: unit * 3,
    },
    dialogFooter: {
      textAlign: 'right',
      '& button:last-child': {
        marginLeft: unit * 2,
      },
    },
    imageButtons: {
      display: 'inline-block',
      float: 'left',
      padding: `0px ${unit / 2}px`,
      minWidth: 0,
      zIndex: 1,
      '& span': {
        transition: 'all 0.25s',
      },
      '&:hover': {
        zIndex: '2 !important',
      },
      '&$bottom': {
        borderRadius: `0px 0px ${unit / 2}px ${unit / 2}px`,
        top: -1,
        position: 'relative',
      },
      '&$left': {
        borderRadius: `${unit / 2}px 0px 0px ${unit / 2}px`,
      },
      '&$right': {
        borderRadius: `0px ${unit / 2}px ${unit / 2}px 0px`,
        left: -1,
        position: 'relative',
      },
      '&$top': {
        borderRadius: `${unit / 2}px ${unit / 2}px 0px 0px`,
      },
    },
    imageButtonsIcon: {
      fontSize: 16,
    },
    imagePreview: {
      border: `1px solid ${colorGrayishBlue}`,
      borderRadius: '50%',
      margin: unit / 2,
      padding: unit / 2,
    },
    imagePreviewWrapper: {
      display: 'inline-block',
      float: 'right',
      maxWidth: 128,
      padding: `0px ${unit * 2}px`,
      textAlign: 'center',
    },
    large: {
      height: 88,
      width: 88,
    },
    left: {},
    medium: {
      height: 64,
      width: 64,
    },
    right: {},
    rotateButtons: {
      display: 'inline-block',
      padding: '8px 0px 0px 94px',
      width: '100%',
    },
    small: {
      height: 48,
      width: 48,
    },
    top: {},
    zoomButtons: {
      display: 'inline-block',
      left: unit,
      position: 'relative',
      top: 20,
      width: unit * 3,
    },
  };
};

class DialogImageEnhancer extends PureComponent {
  state = {
    imageCropped: false,
    rotate: 0,
    scale: 1.2,
  };

  componentWillReceiveProps(nextProps) {
    const { open } = this.props;

    if (open !== nextProps.open) {
      this.setState({ imageCropped: false, rotate: 0, scale: 1.2 });
      this.handleCanvasEditorScrollListener(false);
    }
  }

  handleCanvasEditorScrollListener = (addListener) => {
    const canvasEditor = document.getElementById('canvas-editor');

    if (canvasEditor) {
      if (addListener) {
        canvasEditor.addEventListener('wheel', this.handleCanvasImageZoomMouse);
      } else {
        canvasEditor.removeEventListener('wheel', this.handleCanvasImageZoomMouse);
      }
    }
  };

  handleCanvasImageReady = () => {
    this.handleCanvasEditorScrollListener(true);
    this.handleImagePreview();
  };

  handleCanvasImageRotate = (right) => {
    this.setState((prevState) => {
      let newRotate = prevState.rotate;

      if (right) {
        newRotate = newRotate !== 270 ? newRotate + 90 : 0;
      } else {
        newRotate = newRotate === 0 ? 270 : newRotate - 90;
      }

      return {
        rotate: newRotate,
      };
    });

    setTimeout(this.handleImagePreview, 0);
  };

  handleCanvasImageZoomMouse = (event) => {
    this.handleCanvasImageZoomInOut(event.wheelDelta > 0);
  };

  handleCanvasImageZoomInOut = (zoomIn) => {
    this.setState((prevState) => {
      let newScale = prevState.scale;

      if (zoomIn && newScale < 2) newScale += 0.1;
      if (!zoomIn && newScale > 1.1) newScale -= 0.1;

      return { scale: newScale };
    });

    this.handleImagePreview();
  };

  handleImagePreview = () => {
    if (this.canvasEditor) {
      const canvas = this.canvasEditor.getImage().toDataURL();

      this.setState({ imageCropped: canvas || false });
    }
  };

  handleSubmit = () => {
    const { onRequestClose } = this.props;
    const { imageCropped } = this.state;

    onRequestClose(imageCropped);
  };

  renderDialogFooter() {
    const { classes, onRequestClose } = this.props;

    return (
      <DialogActions className={classes.dialogFooter} disableActionSpacing>
        <Button className={classes.button} color="primary" onClick={() => onRequestClose(false)}>
          Cancel
        </Button>
        <Button className={classes.button} color="primary" onClick={this.handleSubmit} variant="contained">
          Select
        </Button>
      </DialogActions>
    );
  }

  renderPreviewImages() {
    const { classes } = this.props;
    const { imageCropped } = this.state;

    if (!imageCropped) return;

    return (
      <div className={classes.imagePreviewWrapper}>
        <img alt="" className={classnames(classes.imagePreview, classes.large)} src={imageCropped} />
        <img alt="" className={classnames(classes.imagePreview, classes.medium)} src={imageCropped} />
        <img alt="" className={classnames(classes.imagePreview, classes.small)} src={imageCropped} />
      </div>
    );
  }

  renderCanvasEditor() {
    const { classes, source } = this.props;
    const { rotate, scale } = this.state;

    if (!source) return;

    return (
      <div className={classes.canvasEditorWrapper}>
        <AvatarEditor
          border={0}
          borderRadius={112}
          className={classnames(classes.imagePreview, classes.canvasEditor)}
          color={[255, 255, 255, 0.6]}
          height={224}
          id="canvas-editor"
          image={source}
          onImageReady={this.handleCanvasImageReady}
          onMouseUp={this.handleImagePreview}
          ref={(ref) => {
            this.canvasEditor = ref;
          }}
          rotate={rotate}
          scale={scale}
          width={224}
        />
        <div className={classes.zoomButtons}>
          <Button
            className={classnames(classes.imageButtons, classes.top)}
            color="primary"
            disabled={scale >= 2}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() => this.handleCanvasImageZoomInOut(true)}
            variant="outlined"
          >
            <AddIcon className={classes.imageButtonsIcon} />
          </Button>
          <Button
            className={classnames(classes.imageButtons, classes.bottom)}
            color="primary"
            disabled={scale <= 1.1}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() => this.handleCanvasImageZoomInOut(false)}
            variant="outlined"
          >
            <RemoveIcon className={classes.imageButtonsIcon} />
          </Button>
        </div>
        <div className={classes.rotateButtons}>
          <Button
            className={classnames(classes.imageButtons, classes.left)}
            color="primary"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() => this.handleCanvasImageRotate(false)}
            variant="outlined"
          >
            <RotateLeftIcon className={classes.imageButtonsIcon} />
          </Button>
          <Button
            className={classnames(classes.imageButtons, classes.right)}
            color="primary"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() => this.handleCanvasImageRotate(true)}
            variant="outlined"
          >
            <RotateRightIcon className={classes.imageButtonsIcon} />
          </Button>
        </div>
      </div>
    );
  }

  renderDialogContent() {
    const { classes } = this.props;

    return (
      <DialogContent className={classes.dialogContent}>
        {this.renderCanvasEditor()}
        {this.renderPreviewImages()}
      </DialogContent>
    );
  }

  render() {
    const { classes, onRequestClose, open } = this.props;

    return (
      <MuiThemeProvider theme={muiThemeLuma}>
        <Dialog classes={{ paper: classes.dialog }} onClose={() => onRequestClose(false)} open={open}>
          <DialogHeader onRequestClose={() => onRequestClose(false)} title="Image upload" />
          {this.renderDialogContent()}
          {this.renderDialogFooter()}
        </Dialog>
      </MuiThemeProvider>
    );
  }
}

DialogImageEnhancer.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  source: PropTypes.string.isRequired,
};

export default withStyles(styles)(DialogImageEnhancer);
